import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowUUpLeft, FilePdf, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { SectionHeader, Money, useFocusHighlight, ConfirmDialog } from "../../components/shared";
import { DataTable } from "../../components/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { PurchaseDocList } from "./PurchaseDocList";
import { EmailReceiptDialog } from "../../components/EmailReceiptDialog";
import { usePerms } from "../../lib/perms";

export function PurchasePosted() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = searchParams.get("sub") || "receipts";
  const setSub = (v) => { const p = new URLSearchParams(searchParams); p.set("sub", v); setSearchParams(p, { replace: true }); };
  return (
    <div>
      <SectionHeader title="Posted Documents" subtitle="Receipts & archives" />
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="rounded-sm mb-5 flex-wrap h-auto gap-1">
          <TabsTrigger value="receipts" data-testid="tab-posted-receipts" className="rounded-sm">Posted Purchase Receipts</TabsTrigger>
          <TabsTrigger value="pcr" data-testid="tab-posted-purchase-cr" className="rounded-sm">Posted Purchase CR/Adj Notes</TabsTrigger>
          <TabsTrigger value="qarch" data-testid="tab-pquote-archives" className="rounded-sm">Purchase Quote Archives</TabsTrigger>
        </TabsList>
        <TabsContent value="receipts"><PostedReceipts /></TabsContent>
        <TabsContent value="pcr"><PurchaseDocList docType="CreditMemo" variant="posted" /></TabsContent>
        <TabsContent value="qarch"><PurchaseDocList docType="Quote" variant="archive" /></TabsContent>
      </Tabs>
    </div>
  );
}

function PostedReceipts() {
  const { rowProps } = useFocusHighlight();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [undoDoc, setUndoDoc] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);
  const { can } = usePerms();
  const { data: receipts = [] } = useQuery({ queryKey: ["posted-receipts"], queryFn: async () => (await api.get("/posted-receipts")).data });
  const undo = useMutation({
    mutationFn: async (rid) => (await api.post(`/posted-receipts/${rid}/undo`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posted-receipts"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["items"] });
      toast.success("Receipt undone — inventory reversed");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <>
      <DataTable
        testid="posted-receipts-table"
        data={receipts}
        rowKey={(r) => r.id}
        rowTestid="receipt-row"
        rowProps={(r) => { const rp = rowProps(r.id); return { ...rp, onClick: () => navigate(`/purchasing/posted-receipt/${r.id}`), className: `cursor-pointer ${rp.className || ""}` }; }}
        empty="No posted receipts yet."
        columns={[
          { key: "receipt_no", header: "Receipt No.", filter: "text", cellClassName: "font-mono text-xs font-medium", cell: (r) => <span>{r.receipt_no}{r.partial && <span className="ml-1.5 text-[10px] font-sans font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded-sm" data-testid="receipt-partial-badge">Partial</span>}</span> },
          { key: "order_no", header: "Order", filter: "text", cellClassName: "font-mono text-xs" },
          { key: "external_doc_no", header: "External Doc No.", filter: "text", cellClassName: "text-sm text-muted-foreground", cell: (r) => r.external_doc_no || "—" },
          { key: "vendor_name", header: "Vendor", filter: "text", cellClassName: "text-sm font-medium" },
          { key: "date", header: "Date", filter: "text", cellClassName: "text-sm text-muted-foreground font-mono" },
          { key: "total", header: "Total", align: "right", numeric: true, cell: (r) => <Money value={r.total} className="text-sm font-medium" /> },
          { key: "actions", header: "Actions", align: "right", sortable: false, cell: (r) => (
            <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => window.open(`${API}/posted-receipts/${r.id}/pdf`, "_blank")} data-testid="print-receipt" title="Print receipt" className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><FilePdf size={16} weight="bold" /></button>
              {can("purchasing.edit") && <button onClick={() => setEmailDoc(r)} data-testid="email-receipt" title="Email receipt" className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><EnvelopeSimple size={16} weight="bold" /></button>}
              {r.order_no && <button onClick={() => setUndoDoc(r)} data-testid="undo-receipt" title="Undo receipt (reverse inventory)" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><ArrowUUpLeft size={16} weight="bold" /></button>}
            </div>
          ) },
        ]}
      />
      <EmailReceiptDialog receipt={emailDoc} open={!!emailDoc} onOpenChange={(v) => { if (!v) setEmailDoc(null); }} />
      <ConfirmDialog
        open={!!undoDoc}
        onOpenChange={(v) => { if (!v) setUndoDoc(null); }}
        title="Undo this receipt?"
        description={`${undoDoc?.receipt_no} will be reversed: the received quantities are removed from stock and its purchase order reopens. This is blocked if the order has already been invoiced.`}
        confirmLabel="Undo Receipt"
        testid="undo-receipt-confirm"
        onConfirm={() => { undo.mutate(undoDoc.id); setUndoDoc(null); }}
      />
    </>
  );
}
